export const generateFloodFill = (stage, layer, startX, startY, color) => {
    // 1. Create an offscreen canvas to analyze pixels
    const width = stage.width();
    const height = stage.height();
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');

    // Draw the layer to the canvas (excluding the grid if possible, but here we likely draw everything)
    // To avoid filling the grid lines, we might want to hide the grid layer before this snapshot if it's separate?
    // In Whiteboard.jsx, grid is on a separate Layer. We should pass the *Drawing Layer* specifically if we want to fill boundaries there.
    // However, if we want to respect ALL visible boundaries, we grab the stage. 
    // Usually, users want to fill inside lines they drew.

    // Better approach: Draw ONLY the "drawing" elements to the offscreen canvas for analysis.
    // We can iterate elements and draw them to our temp context.
    // But re-rendering everything manually is complex.
    // Easier: Convert Layer to dataURL or image data.

    // Let's use the layer.toCanvas() method from Konva
    const layerCanvas = layer.toCanvas({ pixelRatio: 1 });
    const layerCtx = layerCanvas.getContext('2d');
    const imageData = layerCtx.getImageData(0, 0, width, height);
    const data = imageData.data;

    // 2. Flood Fill Algorithm (BFS) to find the connected area
    // We need to find all pixels connected to (startX, startY) that are "transparent" (or background color)
    // For simplicity, let's assume we fill "transparent" or "white" areas. 

    const stack = [[Math.round(startX), Math.round(startY)]];
    const visited = new Set();
    const mask = new Uint8Array(width * height); // 1 = filled, 0 = empty

    // Helper to get pixel index
    const getIdx = (x, y) => (y * width + x) * 4;

    // Get starting pixel color to match against
    const startIdx = getIdx(Math.round(startX), Math.round(startY));
    const startR = data[startIdx];
    const startG = data[startIdx + 1];
    const startB = data[startIdx + 2];
    const startA = data[startIdx + 3];

    // Threshold for color matching
    const matchColor = (idx) => {
        const r = data[idx];
        const g = data[idx + 1];
        const b = data[idx + 2];
        const a = data[idx + 3];

        // If start was transparent (A=0), we match transparent
        if (startA < 10) return a < 10;

        // Otherwise match color diff
        return Math.abs(r - startR) < 30 &&
            Math.abs(g - startG) < 30 &&
            Math.abs(b - startB) < 30 &&
            Math.abs(a - startA) < 30;
    };

    if (!matchColor(startIdx)) return null; // Clicked on a line already

    const points = [];
    let minX = width, maxX = 0, minY = height, maxY = 0;

    while (stack.length) {
        const [x, y] = stack.pop();
        const key = y * width + x;

        if (mask[key]) continue;

        mask[key] = 1;

        // Update bounds
        minX = Math.min(minX, x);
        maxX = Math.max(maxX, x);
        minY = Math.min(minY, y);
        maxY = Math.max(maxY, y);

        // Check neighbors
        const neighbors = [
            [x + 1, y], [x - 1, y], [x, y + 1], [x, y - 1]
        ];

        for (const [nx, ny] of neighbors) {
            if (nx >= 0 && nx < width && ny >= 0 && ny < height) {
                const nKey = ny * width + nx;
                if (!mask[nKey]) {
                    if (matchColor(getIdx(nx, ny))) {
                        stack.push([nx, ny]);
                    }
                }
            }
        }
    }

    // 3. Trace Boundary aka "Marching Squares" or simpler "Contour Tracing"
    // Since we have a binary mask, we can find the edge points.
    // A simple approach: Scan for the first pixel, trace outline.
    // For this prototype, let's use a simplified approach: 
    // Collect all 'edge' pixels (pixels in mask with at least one neighbor not in mask)
    // Then sort them? No, that gives a point cloud.
    // We need an ordered path.

    // Let's implement a basic contour tracing:
    const contour = traceContour(mask, width, height, minX, minY, maxX, maxY);
    if (!contour || contour.length < 3) return null;

    // 4. Simplify Points to reduce count
    const simplified = simplifyPoints(contour, 2); // 2px tolerance

    return simplified.flatMap(p => [p.x, p.y]);
};

// Simple Moore-Neighbor tracing
const traceContour = (mask, width, height, minX, minY, maxX, maxY) => {
    // Find absolute first point
    let startX = -1, startY = -1;
    outerLoop:
    for (let y = minY; y <= maxY; y++) {
        for (let x = minX; x <= maxX; x++) {
            if (mask[y * width + x]) {
                startX = x;
                startY = y;
                break outerLoop;
            }
        }
    }

    if (startX === -1) return null;

    const points = [];
    let x = startX;
    let y = startY;

    // Directions: N, NE, E, SE, S, SW, W, NW (Clockwise) ? 
    // Actually for Moore, usually: 
    // 0: W, 1: NW, 2: N, 3: NE, 4: E, 5: SE, 6: S, 7: SW
    // Let's stick to standard Moore neighborhood
    // Check order: start from entered pixel logic

    let boundary = [];
    let currX = x;
    let currY = y;
    // Entrance direction (from where we came): initially from West (since we scanned from left)
    let checkDir = 0; // 0=W

    // Max iterations to prevent infinite loop
    let maxIter = (maxX - minX + 1) * (maxY - minY + 1) * 4;

    // Clockwise neighbors offset relative to center
    // 0: (-1, 0) W
    // 1: (-1, -1) NW
    // 2: (0, -1) N
    // 3: (1, -1) NE
    // 4: (1, 0) E
    // 5: (1, 1) SE
    // 6: (0, 1) S
    // 7: (-1, 1) SW
    const offsets = [
        { dx: -1, dy: 0 }, { dx: -1, dy: -1 }, { dx: 0, dy: -1 }, { dx: 1, dy: -1 },
        { dx: 1, dy: 0 }, { dx: 1, dy: 1 }, { dx: 0, dy: 1 }, { dx: -1, dy: 1 }
    ];

    // Start
    boundary.push({ x: currX, y: currY });
    let startPos = { x: currX, y: currY };

    // Backtrack to find a valid neighbor to start (usually just first non-mask)
    // Actually Moore traces pixels *inside* the shape that are on boundary.

    // Let's use a simpler "Walk around the edge" approach since we have full mask access
    // Just walk the perimeter.

    // Current approach might be flaky without robust implementation. 
    // Alternative: library 'd3-contour' or 'marching-squares'. but we can't add deps easily.
    // Let's do a naive edge detection + chain hull? No, concave shapes fail.

    // Revert to reliable boundary tracing:
    // We found the first pixel (S).
    // Let B = S.
    // Let c = W (direction from which we entered S).
    // Repeat: 
    //   Find neighbor of B in clockwise order starting from c that is in Mask.
    //   Let this neighbor be B'.
    //   Update c to point from B' back to B (or similar logic).
    //   B = B'.
    // Until B == S.

    // Directions for offsets (0-7)
    // We entered startX,startY from left, so 'back' is West (dx=-1).
    // We want to check neighbors Clockwise starting from Backtrack Direction.

    let p = { x: startX, y: startY }; // Using p as current point B
    // entry direction into p (from W to E) -> from relative (-1,0)
    // Initial backtrack direction: where we came FROM. relative to p. (-1, 0) is index 0.
    // We start searching for NEXT pixel clockwise from "fromDir"

    let fromDir = 0; // West

    for (let i = 0; i < maxIter; i++) {
        let foundNext = false;
        // Check 8 neighbors in clockwise order
        // Starting from the one *after* where we came from? 
        // Moore algorithm: start search at backtrack direction.

        // Actually usually we search starting from (backtrack + 1) % 8?
        // Let's check typical Moore Neighbor Tracing

        let startSearchDir = fromDir;

        for (let j = 0; j < 8; j++) {
            // Clockwise from startSearchDir
            let checkIdx = (startSearchDir + j) % 8;
            let off = offsets[checkIdx];
            let nx = p.x + off.dx;
            let ny = p.y + off.dy;

            if (nx >= 0 && nx < width && ny >= 0 && ny < height && mask[ny * width + nx]) {
                // Found next boundary pixel
                boundary.push({ x: nx, y: ny });
                p = { x: nx, y: ny };
                // New backtrack direction is opposite of current move direction?
                // checkIdx is direction FROM old TO new.
                // New 'from' is opposite of checkIdx. 
                // Wait, Moore improves this: new search start is neighbor PREVIOUS to the one entering?
                // Standard: Backtrack = (checkIdx + 4) % 8. start search here? 
                // Usually start from (checkIdx + 4 + 1) % 8 or similar.
                // Let's try: fromDir = (checkIdx + 4 + 2) % 8; // Start checking from 'left' of entry

                // Let's use specific logic:
                // Entering 'p' from direction 'off' (index checkIdx).
                // Next search should start "outside" and sweep in.
                // Inverse of checkIdx is (checkIdx + 4) % 8.
                // We want to start search counter-clockwise from the entry?
                // Let's try: Next search start = (checkIdx + 5) % 8? (approx -3 spots)

                // Working heuristic for 8-connected: 
                // Enter from d. Scan clockwise starting at (d + 2) % 8? NO.
                // Let's stick to: scan clockwise starting from (enter_direction + 6) % 8 ?? 
                // Let's just create a robust 'follow' loop:
                // Scan neighbors of current P clockwise.
                // Take the first one that is 1.
                // HOWEVER, this blindly follows and might get stuck or cut inside.
                // We must start scan from "known empty" side.

                // Simple version:
                // We just entered P from 'fromDir' (relative offset index connected to P).
                // Example: We moved East (idx 4). We entered P. Previous was West of P (idx 0).
                // We should start searching neighbors of P starting from the empty space we just passed?
                // Let's assume (checkIdx + 4 + 1) % 8.

                let enterDir = (checkIdx + 4) % 8;
                fromDir = (enterDir + 2) % 8; // Heuristic

                foundNext = true;
                break;
            }
        }

        if (!foundNext) break; // Isolated pixel

        if (p.x === startX && p.y === startY) break; // Loop closed
    }

    return boundary;
};

// Ramer-Douglas-Peucker simplification
const simplifyPoints = (points, tolerance) => {
    if (points.length <= 2) return points;

    const sqTolerance = tolerance * tolerance;

    // Find point with max distance from line segment [first, last]
    let maxSqDist = 0;
    let index = 0;
    const end = points.length - 1;

    for (let i = 1; i < end; i++) {
        const sqDist = getSqSegDist(points[i], points[0], points[end]);
        if (sqDist > maxSqDist) {
            maxSqDist = sqDist;
            index = i;
        }
    }

    if (maxSqDist > sqTolerance) {
        const left = simplifyPoints(points.slice(0, index + 1), tolerance);
        const right = simplifyPoints(points.slice(index), tolerance);
        return [...left.slice(0, left.length - 1), ...right];
    }

    return [points[0], points[end]];
};

const getSqSegDist = (p, p1, p2) => {
    let x = p1.x, y = p1.y,
        dx = p2.x - x, dy = p2.y - y;

    if (dx !== 0 || dy !== 0) {
        const t = ((p.x - x) * dx + (p.y - y) * dy) / (dx * dx + dy * dy);
        if (t > 1) {
            x = p2.x; y = p2.y;
        } else if (t > 0) {
            x += dx * t;
            y += dy * t;
        }
    }

    dx = p.x - x;
    dy = p.y - y;

    return dx * dx + dy * dy;
};
